import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { usePaginatedGinSteps } from '@hellodarwin/app-features/lib/features/gin-steps/paginated-gin-steps/provider';
import scrollElementIntoView from '@hellodarwin/app-features/lib/utils/dom/scroll-element-into-view';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import {
  getGinStepAssignedContactIds,
  isGinStepAssignedToContact,
} from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import Check from '@hellodarwin/icons/dist/icons/Check';
import Warning from '@hellodarwin/icons/dist/icons/Warning';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useAppSelector } from '../../../../../app/index';
import { selectProfile } from '../../../../../features/api/slices/profile-slice';
import {
  Connector,
  ProgressContainer,
  StepCircle,
  StepsProgressLayout,
  WarningCircle,
} from './styles';

interface StepsProgressProps {
  hasCustomApplicationAccess?: boolean;
}

const StepsProgress: React.FC<StepsProgressProps> = ({
  hasCustomApplicationAccess,
}) => {
  const { allSteps, goToStepIndex, currentStepIndex } = usePaginatedGinSteps();
  const theme = useTheme();
  const count = allSteps.length;
  const completedFlags = useMemo(
    () => allSteps.map((entry) => Boolean(entry.step?.completed_at)),
    [allSteps],
  );

  const profile = useAppSelector(selectProfile);

  const isOwnerClientFlags = useMemo(
    () =>
      allSteps.map((entry) =>
        Boolean(
          entry.step?.owner === 'Client' &&
            (getGinStepAssignedContactIds(entry.step).length === 0 ||
              isGinStepAssignedToContact(entry.step, profile.contact_id)),
        ),
      ),
    [allSteps, profile],
  );
  const { accessLevel } = usePremiumAccess();
  const customApplicationAccess =
    hasCustomApplicationAccess === true || accessLevel === 'custom';

  if (!count) return null;

  return (
    <StepsProgressLayout>
      <Typography elementTheme="subtitle1">
        {t('gin_steps|application_steps')}
      </Typography>
      <ProgressContainer>
        <Connector />
        {allSteps.map((entry, idx) => {
          const isCompleted = completedFlags[idx];
          const isOwnerClient = isOwnerClientFlags[idx];
          const isCurrent = idx === currentStepIndex;
          const canClick = isOwnerClient || !customApplicationAccess;
          return (
            <StepCircle
              key={entry.step.step_id}
              $completed={isCompleted}
              $current={isCurrent}
              onClick={
                canClick
                  ? () => {
                      goToStepIndex(idx);
                      const entry = allSteps[idx];
                      const elementId = entry
                        ? `step-${entry.pageIndex + 1}-${entry.stepIndexInPage + 1}`
                        : `step-${idx + 1}`;
                      scrollElementIntoView({
                        containerId: 'sidebar-page-body',
                        elementId,
                        padding: theme.spacing.xxxl,
                      });
                    }
                  : undefined
              }
              role={canClick ? 'button' : undefined}
              tabIndex={canClick ? 0 : undefined}
              style={{
                cursor: canClick ? 'pointer' : 'default',
              }}
            >
              {isCompleted ? (
                <Check
                  size={12}
                  style={{ fontWeight: 'bold' }}
                  color={theme.colors.green_high_contrast}
                  strokeWidth={isCurrent ? 4 : 2.5}
                />
              ) : (
                <span>{idx + 1}</span>
              )}
              {!isCompleted && isOwnerClient && customApplicationAccess && (
                <WarningCircle>
                  <Warning size={12} width={50} />
                </WarningCircle>
              )}
            </StepCircle>
          );
        })}
      </ProgressContainer>
    </StepsProgressLayout>
  );
};

export default StepsProgress;
